import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import type { ApiEnvelope, ApiFieldError, Paginated } from './types';

const TOKEN_STORAGE_KEY = 'bfc.crm.token';

export const tokenStorage = {
  get: (): string | null => localStorage.getItem(TOKEN_STORAGE_KEY),
  set: (token: string): void => localStorage.setItem(TOKEN_STORAGE_KEY, token),
  clear: (): void => localStorage.removeItem(TOKEN_STORAGE_KEY),
};

export const http = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

http.interceptors.request.use((config) => {
  const token = tokenStorage.get();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/** Raised for every non-2xx response so callers get a consistent shape. */
export class ApiError extends Error {
  readonly status: number;
  readonly fieldErrors: ApiFieldError[];

  constructor(message: string, status: number, fieldErrors: ApiFieldError[] = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.fieldErrors = fieldErrors;
  }

  /** Field-keyed messages, ready to feed into form helper text. */
  byField(): Record<string, string> {
    return Object.fromEntries(this.fieldErrors.map((error) => [error.field, error.message]));
  }
}

let onUnauthorized: (() => void) | null = null;

/** Registered by the auth provider so an expired token returns the user to login. */
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

http.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ message?: string; errors?: ApiFieldError[] }>) => {
    const status = error.response?.status ?? 0;

    if (status === 401) {
      tokenStorage.clear();
      onUnauthorized?.();
    }

    const message =
      error.response?.data?.message ??
      (status === 0
        ? 'Unable to reach the server. Check your connection and try again.'
        : 'Something went wrong. Please try again.');

    return Promise.reject(new ApiError(message, status, error.response?.data?.errors ?? []));
  },
);

export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await http.get<ApiEnvelope<T>>(url, config);
  return data.data;
}

export async function getPaginated<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<Paginated<T>> {
  const { data } = await http.get<ApiEnvelope<T[]>>(url, config);
  return {
    items: data.data,
    meta: data.meta ?? { page: 1, pageSize: data.data.length, total: data.data.length, totalPages: 1 },
    message: data.message,
  };
}

export async function post<T>(url: string, body?: unknown): Promise<{ data: T; message: string }> {
  const { data } = await http.post<ApiEnvelope<T>>(url, body);
  return { data: data.data, message: data.message };
}

export async function put<T>(url: string, body?: unknown): Promise<{ data: T; message: string }> {
  const { data } = await http.put<ApiEnvelope<T>>(url, body);
  return { data: data.data, message: data.message };
}
export async function del<T>(url: string): Promise<{ data: T; message: string }> {
  const { data } = await http.delete<ApiEnvelope<T>>(url);
  return { data: data.data, message: data.message };
}

/** Downloads a binary export and hands it to the browser as a file. */
export async function downloadFile(
  url: string,
  params: Record<string, unknown>,
  fallbackName: string,
): Promise<void> {
  const response = await http.get<Blob>(url, { params, responseType: 'blob' });

  const disposition = response.headers['content-disposition'] as string | undefined;
  const match = disposition?.match(/filename="?([^"]+)"?/);
  const fileName = match?.[1] ?? fallbackName;

  const objectUrl = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

/** Drops empty values so they never appear as blank query parameters. */
export function cleanParams(params: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  );
}

